// src/pages/planejamento/AtendimentosVisitasPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { collection, doc, getDoc, getDocs, limit, query, startAfter, updateDoc, where, serverTimestamp } from "firebase/firestore";
import { useNavigate } from "react-router-dom";

import LayoutDesktop from "../../layouts/LayoutDesktop";
import LayoutMobile from "../../layouts/LayoutMobile";
import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../contexts/AuthContext";
import { useIsMobile } from "../../hooks/useIsMobile";
import { db } from "../../config/firebase";
import { marcarPropostasFinalizadasPorSessao } from "../../services/propostasStatusService";
import { NIVEIS } from "../../config/niveis";
import { usePlanejamentoAtendimentos } from "../../hooks/usePlanejamentoAtendimentos";
import { nivelUsuario, pode } from "../../utils/permissoes";
import { sincronizarPlanejamentoContratoAprovado } from "../../services/abrirPreventivasClienteApi";
import "./AtendimentosVisitasPage.css";

const STATUS_OPTIONS = [
  { value: "ATIVAS", label: "Ativas" },
  { value: "PLANEJADA", label: "Planejada" },
  { value: "AGENDADA", label: "Agendada" },
  { value: "ATENDIDA", label: "Atendida" },
  { value: "FINALIZADA", label: "Finalizada" },
  { value: "TODOS", label: "Todos" },
];

const RELATORIO_EMPRESA_DEFAULT = {
  empresaNome: "Ramalho ProFit",
  empresaSubtitulo: "Montagem & Manutenção de Equipamentos Fitness",
  empresaCnpj: "29.252.847/0001-45",
  empresaTelefone: "(11) 95679-8125",
  empresaInstagram: "@ramalhoprofit",
  empresaEmail: "contato@ramalhoprofit.com.br",
  empresaSite: "www.ramalhoprofit.com.br",
  empresaEndereco: "São Paulo - SP",
};

function formatDateTime(value) {
  if (!value) return "-";
  const dt = typeof value?.toDate === "function" ? value.toDate() : new Date(value);
  if (Number.isNaN(dt.getTime())) return "-";
  return dt.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function monthString(date = new Date()) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
}

function normStr(v) {
  return String(v || "").trim().toLowerCase();
}

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

async function toDataUrl(imageUrl) {
  if (!imageUrl) return "";
  try {
    const response = await fetch(imageUrl, { mode: "cors" });
    if (!response.ok) return imageUrl;
    const blob = await response.blob();
    const dataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
    return typeof dataUrl === "string" ? dataUrl : imageUrl;
  } catch {
    return imageUrl;
  }
}

function isContratoAvulso(tipo) {
  return normStr(tipo).includes("avulso");
}

function isContratoQuinzenal(tipo) {
  return normStr(tipo).includes("quinzen");
}

function toDateTimeInput(value) {
  if (!value) return "";
  const dt = typeof value?.toDate === "function" ? value.toDate() : new Date(value);
  if (Number.isNaN(dt.getTime())) return "";
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const d = String(dt.getDate()).padStart(2, "0");
  const hh = String(dt.getHours()).padStart(2, "0");
  const mm = String(dt.getMinutes()).padStart(2, "0");
  return `${y}-${m}-${d}T${hh}:${mm}`;
}

function competenciaFromContratoEData(contratoTipo, inicioDate) {
  if (!(inicioDate instanceof Date) || Number.isNaN(inicioDate.getTime())) return "";
  const y = inicioDate.getFullYear();
  const m = String(inicioDate.getMonth() + 1).padStart(2, "0");
  const base = `${y}-${m}`;
  if (isContratoQuinzenal(contratoTipo)) {
    const q = inicioDate.getDate() <= 15 ? "Q1" : "Q2";
    return `${base}-${q}`;
  }
  return base;
}

function normalizarStatusVisita(sessao) {
  const raw = String(sessao?.status || "").trim().toUpperCase();
  if (raw === "FINALIZADA") return "FINALIZADA";
  if (raw === "ATENDIDA" || raw === "REALIZADA") return "ATENDIDA";
  if (raw === "AGENDADA") return "AGENDADA";
  if (raw === "CANCELADA") return "CANCELADA";
  if (sessao?.tecnicoId) return "AGENDADA";
  return "PLANEJADA";
}

function filtrarVisitasPorStatus(visitas, statusFiltro) {
  const status = String(statusFiltro || "ATIVAS").toUpperCase();
  if (status === "TODOS") return visitas;
  if (status === "ATIVAS") return visitas.filter((v) => String(v.status || "").toUpperCase() !== "FINALIZADA");
  return visitas.filter((v) => String(v.status || "").toUpperCase() === status);
}

function normalizarTipoItem(item) {
  const tipoRaw = String(item?.tipo || "").toUpperCase();
  if (tipoRaw === "OS" || item?.osId) return "OS";
  if (tipoRaw === "PREVENTIVA" || item?.preventivaId) return "PREVENTIVA";
  if (tipoRaw === "INSTALACAO" || item?.checklistInstalacaoId || item?.propostaInstalacaoId) return "INSTALACAO";
  return "EQUIPAMENTO";
}

function obterTipoVisita(itens = []) {
  const tipos = new Set((itens || []).map((i) => normalizarTipoItem(i)).filter(Boolean));
  return tipos.size === 1 ? Array.from(tipos)[0] : null;
}

export default function AtendimentosVisitasPage() {
  const { tema, alterarTema } = useTheme();
  const { usuario } = useAuth();
  const navigate = useNavigate();  const isMobile = useIsMobile(900);
  const {
    scope,
    criarAtendimento,
    listarAtendimentosPorClienteCompetencia,
    listarSessoesDoAtendimento,
    listarItensSessao,
    adicionarItensSessao,
    removerItensSessao,
    contarItensSessao,
    excluirSessao,
  } = usePlanejamentoAtendimentos();

  const [clientes, setClientes] = useState([]);
  const [tecnicos, setTecnicos] = useState([]);
  const [academias, setAcademias] = useState([]);
  const [academiasMap, setAcademiasMap] = useState({});

  const [clienteId, setClienteId] = useState("");
  const [competenciaMes, setCompetenciaMes] = useState(monthString());
  const [statusFiltro, setStatusFiltro] = useState("ATIVAS");
  const [quinzenaFiltro, setQuinzenaFiltro] = useState("TODAS");
  const [academiaId, setAcademiaId] = useState("");

  const [atendimentos, setAtendimentos] = useState([]);
  const [sessaoCounts, setSessaoCounts] = useState({});
  const [loadingAtendimentos, setLoadingAtendimentos] = useState(false);
  const [visitas, setVisitas] = useState([]);
  const [loadingVisitas, setLoadingVisitas] = useState(false);
  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState("");

  const [atendimentoSelecionado, setAtendimentoSelecionado] = useState(null);
  const [sessoes, setSessoes] = useState([]);
  const [sessaoItensCount, setSessaoItensCount] = useState({});
  const [loadingSessoes, setLoadingSessoes] = useState(false);

  const [sessaoSelecionada, setSessaoSelecionada] = useState(null);
  const [sessaoItens, setSessaoItens] = useState([]);
  const [loadingSessaoItens, setLoadingSessaoItens] = useState(false);
  const [salvandoSessaoItens, setSalvandoSessaoItens] = useState(false);
  const [sessaoExpandidaId, setSessaoExpandidaId] = useState(null);
  const [loadingSessaoExpandidaId, setLoadingSessaoExpandidaId] = useState("");
  const [sessaoItensExpandidaMap, setSessaoItensExpandidaMap] = useState({});

  const [equipamentos, setEquipamentos] = useState([]);
  const [setores, setSetores] = useState([]);
  const [loadingEquipamentos, setLoadingEquipamentos] = useState(false);
  const [equipamentosCursor, setEquipamentosCursor] = useState(null);
  const [equipamentosHasMore, setEquipamentosHasMore] = useState(false);
  const [loadingEquipamentosMore, setLoadingEquipamentosMore] = useState(false);

  const EQUIPAMENTOS_PAGE_SIZE = 120;

  // Backlog (itens) pode ser Preventivas (equipamentos) e/ou OS corretivas
  const [backlogTipo, setBacklogTipo] = useState("PREVENTIVA");
  const [osAbertas, setOsAbertas] = useState([]);
  const [loadingOs, setLoadingOs] = useState(false);

  const [preventivas, setPreventivas] = useState([]);
  const [loadingPreventivas, setLoadingPreventivas] = useState(false);

  // Criação de sessão/visita agora é centralizada em "Nova Visita" (com itens),
  // evitando sessões vazias criadas por telas diferentes.
  const [excluindoSessao, setExcluindoSessao] = useState(false);
  const [concluindoAtendimento, setConcluindoAtendimento] = useState(false);
  const [gerandoRelatorio, setGerandoRelatorio] = useState(false);
  const [relatorioEmpresaCfg, setRelatorioEmpresaCfg] = useState(RELATORIO_EMPRESA_DEFAULT);
  const [sidebarAberta, setSidebarAberta] = useState(false);

  const novaVisitaHref = useMemo(() => {
    const qs = new URLSearchParams();
    if (clienteId) qs.set("clienteId", clienteId);

    // Pré-preencher quando a tela está focada em um atendimento
    const contratoTipoSel = atendimentoSelecionado?.contratoTipo || "";
    if (contratoTipoSel) qs.set("contratoTipo", contratoTipoSel);

    const compSel = String(atendimentoSelecionado?.competencia || "");
    const mesSel = compSel ? compSel.slice(0, 7) : "";
    const qSel = compSel.includes("-Q2") ? "Q2" : (compSel.includes("-Q1") ? "Q1" : "");

    if (mesSel) qs.set("competenciaMes", mesSel);
    else if (competenciaMes) qs.set("competenciaMes", competenciaMes);

    if (qSel) qs.set("quinzena", qSel);
    else if (quinzenaFiltro === "Q1" || quinzenaFiltro === "Q2") qs.set("quinzena", quinzenaFiltro);

    if (academiaId) qs.set("academiaId", academiaId);

    // Voltar para esta tela após salvar
    qs.set("returnTo", "/planejamento/atendimentos-visitas");

    const qstr = qs.toString();
    return `/planejamento/nova-visita${qstr ? `?${qstr}` : ""}`;
  }, [clienteId, atendimentoSelecionado?.id, atendimentoSelecionado?.competencia, atendimentoSelecionado?.contratoTipo, competenciaMes, quinzenaFiltro, academiaId]);

  const agendamentoTecnicosHref = "/planejamento/agendamentos-tecnicos";

  const navigateWithFallback = (href, selector) => {
    if (!href) return;
    navigate(href);
    if (typeof window === "undefined" || typeof document === "undefined" || !selector) return;

    const scheduleCheck = () => {
      try {
        if (!document.querySelector(selector)) {
          window.location.assign(href);
        }
      } catch {
        window.location.assign(href);
      }
    };

    if (typeof window.requestAnimationFrame === "function") {
      window.requestAnimationFrame(() => {
        window.setTimeout(scheduleCheck, 180);
      });
    } else {
      window.setTimeout(scheduleCheck, 180);
    }
  };

  const abrirNovaVisita = () => navigateWithFallback(novaVisitaHref, ".nova-visita-card");

  const abrirAgendamentoTecnicos = () => navigateWithFallback(agendamentoTecnicosHref, ".agendamentos-container");

  const canAgendamentoTecnicos = useMemo(
    () => pode(usuario, "planejamento", "agendamentos", "tabela"),
    [usuario]
  );
  const competenciaFiltro = useMemo(() => {
    if (!competenciaMes) return "";
    if (quinzenaFiltro === "Q1" || quinzenaFiltro === "Q2") {
      return `${competenciaMes}-${quinzenaFiltro}`;
    }
    return competenciaMes;
  }, [competenciaMes, quinzenaFiltro]);

  const clientesById = useMemo(() => {
    const map = {};
    clientes.forEach((c) => {
      map[String(c.id)] = c;
    });
    return map;
  }, [clientes]);

  const canGlobalView = useMemo(() => {
    const nivel = nivelUsuario(usuario);
    return (
      usuario?.acessoGlobal === true
      || usuario?.globalAccess === true
      || usuario?.tipoAcesso === "GLOBAL"
      || nivel >= NIVEIS.ADMIN
    );
  }, [usuario]);

  const empresaIdForOps = useMemo(
    () => scope?.empresaId || atendimentoSelecionado?.clienteId || clienteId || "",
    [scope?.empresaId, atendimentoSelecionado?.clienteId, clienteId]
  );

  const canDeleteSessao = useMemo(() => {
    const nivel = nivelUsuario(usuario);
    return nivel >= NIVEIS.ADMIN;
  }, [usuario]);

  useEffect(() => {
    if (!scope?.grupoId) {
      setRelatorioEmpresaCfg(RELATORIO_EMPRESA_DEFAULT);
      return;
    }

    let alive = true;

    (async () => {
      try {
        const grupoSnap = await getDoc(doc(db, "grupos", scope.grupoId));
        if (!alive) return;
        if (!grupoSnap.exists()) {
          setRelatorioEmpresaCfg(RELATORIO_EMPRESA_DEFAULT);
          return;
        }
        const data = grupoSnap.data() || {};
        const rel = data.relatorios || {};
        setRelatorioEmpresaCfg({
          empresaNome: String(rel.empresaNome || RELATORIO_EMPRESA_DEFAULT.empresaNome),
          empresaSubtitulo: String(rel.empresaSubtitulo || RELATORIO_EMPRESA_DEFAULT.empresaSubtitulo),
          empresaCnpj: String(rel.empresaCnpj || RELATORIO_EMPRESA_DEFAULT.empresaCnpj),
          empresaTelefone: String(rel.empresaTelefone || RELATORIO_EMPRESA_DEFAULT.empresaTelefone),
          empresaInstagram: String(rel.empresaInstagram || RELATORIO_EMPRESA_DEFAULT.empresaInstagram),
          empresaEmail: String(rel.empresaEmail || RELATORIO_EMPRESA_DEFAULT.empresaEmail),
          empresaSite: String(rel.empresaSite || RELATORIO_EMPRESA_DEFAULT.empresaSite),
          empresaEndereco: String(rel.empresaEndereco || RELATORIO_EMPRESA_DEFAULT.empresaEndereco),
        });
      } catch {
        if (!alive) return;
        setRelatorioEmpresaCfg(RELATORIO_EMPRESA_DEFAULT);
      }
    })();

    return () => {
      alive = false;
    };
  }, [scope?.grupoId]);


  const tecnicosByRef = useMemo(() => {
    const map = {};
    tecnicos.forEach((t) => {
      const id = String(t.id || "").trim();
      const uid = String(t.uid || t.userId || t.authUid || "").trim();
      if (id) map[id] = t;
      if (uid) map[uid] = t;
    });
    return map;
  }, [tecnicos]);

  const getTecnicoNome = (tecnicoId) => {
    const key = String(tecnicoId || "").trim();
    if (!key) return "-";
    return tecnicosByRef[key]?.nome || tecnicosByRef[key]?.email || "-";
  };

  const setoresById = useMemo(() => {
    const map = {};
    setores.forEach((s) => {
      map[String(s.id)] = s;
    });
    return map;
  }, [setores]);

  const equipamentosById = useMemo(() => {
    const map = {};
    equipamentos.forEach((e) => {
      map[String(e.id)] = e;
    });
    return map;
  }, [equipamentos]);

  useEffect(() => {
    if (!scope?.grupoId) return;

    const carregarClientes = async () => {
      try {
        const q = query(collection(db, "empresas"), where("grupoId", "==", scope.grupoId));
        const snap = await getDocs(q);
        const lista = snap.docs.map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR", { sensitivity: "base" }));
        setClientes(lista);
      } catch (e) {
        console.error("Erro ao carregar clientes:", e);
      }
    };

    const carregarTecnicos = async () => {
      try {
        const q = query(
          collection(db, "usuarios"),
          where("grupoId", "==", scope.grupoId)
        );
        const snap = await getDocs(q);
        const lista = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((u) => nivelUsuario(u) >= NIVEIS.TECNICO)
          .sort((a, b) => String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR", { sensitivity: "base" }));
        setTecnicos(lista);
      } catch (e) {
        console.error("Erro ao carregar tecnicos:", e);
      }
    };

    carregarClientes();
    carregarTecnicos();
  }, [scope?.grupoId]);

  useEffect(() => {
    if (!scope?.grupoId || !clienteId) {
      setAcademias([]);
      setAcademiaId("");
      return;
    }

    (async () => {
      try {
        const q = query(
          collection(db, "setores"),
          where("grupoId", "==", scope.grupoId),
          where("empresaId", "==", clienteId)
        );
        const snap = await getDocs(q);
        const lista = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR", { sensitivity: "base" }));
        setAcademias(lista);
      } catch (e) {
        console.error("Erro ao carregar academias:", e);
        setAcademias([]);
      }
    })();
  }, [scope?.grupoId, clienteId]);

  useEffect(() => {
    if (!scope?.grupoId) {
      setAcademiasMap({});
      return;
    }

    (async () => {
      try {
        const snap = await getDocs(
          query(collection(db, "setores"), where("grupoId", "==", scope.grupoId))
        );
        const map = {};
        snap.docs.forEach((d) => {
          const data = d.data() || {};
          map[d.id] = data.nome || data.codigo || d.id;
        });
        setAcademiasMap(map);
      } catch (e) {
        console.error("Erro ao carregar mapa global de academias:", e);
        setAcademiasMap({});
      }
    })();
  }, [scope?.grupoId]);

  const filtrarLista = (lista, overrides = {}) => {
    const competenciaMesValue = overrides.competenciaMesValue ?? competenciaMes;
    const quinzenaFiltroValue = overrides.quinzenaFiltroValue ?? quinzenaFiltro;
    const competenciaFiltroLocal = (competenciaMesValue && (quinzenaFiltroValue === "Q1" || quinzenaFiltroValue === "Q2"))
      ? `${competenciaMesValue}-${quinzenaFiltroValue}`
      : competenciaMesValue;

    let base = [...lista];
    if (competenciaMesValue) {
      if (quinzenaFiltroValue === "Q1" || quinzenaFiltroValue === "Q2") {
        base = base.filter((a) => String(a.competencia || "").toUpperCase() === String(competenciaFiltroLocal || "").toUpperCase());
      } else {
        base = base.filter((a) => String(a.competencia || "").startsWith(competenciaMesValue));
      }
    }
    return base;
  };

  const carregarAtendimentos = async (overrides = {}) => {
    const clienteIdValue = overrides.clienteIdValue ?? clienteId;
    const academiaIdValue = overrides.academiaIdValue ?? academiaId;
    const allowGlobal = canGlobalView && !clienteIdValue;
    if (!clienteIdValue && !allowGlobal) return;
    const empresaIdValue = scope?.empresaId || clienteIdValue || null;
    if (!scope?.grupoId || (!empresaIdValue && !allowGlobal)) {
      setErro("Selecione uma empresa para carregar atendimentos.");
      return;
    }
    setLoadingAtendimentos(true);
    setLoadingVisitas(true);
    setErro("");
    try {
      const lista = await listarAtendimentosPorClienteCompetencia(clienteIdValue, "", {
        empresaId: empresaIdValue || undefined,
        allowGlobal,
      });
      const filtrados = filtrarLista(lista, overrides);
      setAtendimentos(filtrados);

      const counts = {};
      const visitasTmp = [];
      await Promise.all(
        filtrados.map(async (at) => {
          try {
            const sess = await listarSessoesDoAtendimento(at.id, {
              empresaId: at.empresaId || empresaIdValue,
            });
            counts[at.id] = sess.length;

            await Promise.all(
              sess.map(async (s) => {
                let itensCount = 0;
                if (academiaIdValue) {
                  try {
                    const itens = await listarItensSessao(s.id, {
                      empresaId: at.empresaId || empresaIdValue,
                    });
                    const filtradosItens = itens.filter((item) => String(item.setorId || "") === String(academiaIdValue));
                    itensCount = filtradosItens.length;
                    if (itensCount === 0) return;
                  } catch {
                    itensCount = 0;
                    return;
                  }
                } else {
                  try {
                    itensCount = await contarItensSessao(s.id, {
                      empresaId: at.empresaId || empresaIdValue,
                    });
                  } catch {
                    itensCount = 0;
                  }
                }
                visitasTmp.push({
                  id: s.id,
                  atendimentoId: at.id,
                  clienteId: at.clienteId,
                  academiaId: at.academiaId || at.setorId || s.academiaId || s.setorId || null,
                  academiaNome: at.academiaNome || at.setorNome || s.academiaNome || s.setorNome || "",
                  competencia: at.competencia,
                  contratoTipo: at.contratoTipo,
                  tecnicoId: s.tecnicoId,
                  inicioEm: s.inicioEm,
                  fimEm: s.fimEm,
                  status: normalizarStatusVisita(s),
                  observacoes: s.observacoes,
                  itensCount,
                });
              })
            );
          } catch {
            counts[at.id] = 0;
          }
        })
      );
      setSessaoCounts(counts);
      visitasTmp.sort((a, b) => {
        const da = a.inicioEm?.toDate ? a.inicioEm.toDate() : new Date(a.inicioEm || 0);
        const dbv = b.inicioEm?.toDate ? b.inicioEm.toDate() : new Date(b.inicioEm || 0);
        return dbv.getTime() - da.getTime();
      });
      setVisitas(filtrarVisitasPorStatus(visitasTmp, statusFiltro));
    } catch (e) {
      console.error("Erro ao carregar atendimentos:", e);
      setErro("Erro ao carregar atendimentos.");
      setAtendimentos([]);
      setSessaoCounts({});
      setVisitas([]);
    } finally {
      setLoadingAtendimentos(false);
      setLoadingVisitas(false);
    }
  };

  const limparSelecaoVisita = () => {
    setSessaoSelecionada(null);
    setAtendimentoSelecionado(null);
    setSessoes([]);
    setSessaoExpandidaId(null);
    setSessaoItensExpandidaMap({});
    setSessaoItens([]);
    setSessaoItensCount({});
  };

  useEffect(() => {
    carregarAtendimentos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clienteId, competenciaMes, statusFiltro, quinzenaFiltro, academiaId, scope?.grupoId, scope?.empresaId, canGlobalView]);

  useEffect(() => {
    if (loadingAtendimentos || loadingVisitas) return;
    if (!atendimentoSelecionado?.id || !sessaoSelecionada?.id) return;

    const visitaAindaVisivel = visitas.some(
      (v) => String(v.atendimentoId) === String(atendimentoSelecionado.id)
        && String(v.id) === String(sessaoSelecionada.id)
    );

    if (!visitaAindaVisivel) {
      limparSelecaoVisita();
    }
  }, [
    visitas,
    loadingAtendimentos,
    loadingVisitas,
    atendimentoSelecionado?.id,
    sessaoSelecionada?.id,
  ]);

  const abrirAtendimento = async (at) => {
    const empresaIdValue = scope?.empresaId || at?.empresaId || at?.clienteId || "";
    setAtendimentoSelecionado(at);
    setSessaoSelecionada(null);
    setSessaoExpandidaId(null);
    setSessaoItensExpandidaMap({});
    setSessaoItens([]);
    setSessaoItensCount({});
    setLoadingSessoes(true);
    try {
      const lista = await listarSessoesDoAtendimento(at.id, { empresaId: empresaIdValue });
      setSessoes(lista);

      const counts = {};
      await Promise.all(
        lista.map(async (sess) => {
          try {
            counts[sess.id] = await contarItensSessao(sess.id, { empresaId: empresaIdValue });
          } catch {
            counts[sess.id] = 0;
          }
        })
      );
      setSessaoItensCount(counts);
      return lista;
    } catch (e) {
      console.error("Erro ao carregar sessoes:", e);
      setSessoes([]);
      setSessaoItensCount({});
      return [];
    } finally {
      setLoadingSessoes(false);
    }
  };

  const abrirVisita = async (visita) => {
    const isMesmaVisitaSelecionada =
      atendimentoSelecionado?.id === visita?.atendimentoId
      && sessaoSelecionada?.id === visita?.id;

    if (isMesmaVisitaSelecionada) {
      limparSelecaoVisita();
      return;
    }

    const at = atendimentos.find((a) => a.id === visita.atendimentoId) || null;
    if (!at) return;

    const lista = await abrirAtendimento(at);
    const sess = lista.find((s) => s.id === visita.id) || null;
    if (sess) {
      setSessaoSelecionada(sess);
    }
  };

  const handleExcluirSessao = async (sessaoId, atendimentoId) => {
    if (!canDeleteSessao) return;
    const atendimento = atendimentos.find((a) => a.id === atendimentoId) || null;
    if (!sessaoId) return;

    const clienteNome = clientesById[atendimento?.clienteId]?.nome || "Cliente";
    const ok = window.confirm(
      `Excluir sessao de ${clienteNome} (${atendimento?.competencia || "-"})? Isso remove os itens da sessao.`
    );
    if (!ok) return;

    setExcluindoSessao(true);
    setErro("");
    try {
      const empresaIdValue = atendimento?.empresaId || atendimento?.clienteId || empresaIdForOps || null;
      await excluirSessao(sessaoId, { empresaId: empresaIdValue });
      if (sessaoSelecionada?.id === sessaoId) {
        setSessaoSelecionada(null);
        setSessaoItens([]);
      }
      if (sessaoExpandidaId === sessaoId) {
        setSessaoExpandidaId(null);
      }
      setSessaoItensExpandidaMap((prev) => {
        const next = { ...prev };
        delete next[sessaoId];
        return next;
      });
      await carregarAtendimentos();
      setSucesso("Sessao excluida com sucesso.");
    } catch (e) {
      console.error("Erro ao excluir sessao:", e);
      setErro("Erro ao excluir sessao.");
    } finally {
      setExcluindoSessao(false);
    }
  };

  // Regra: cliente Avulso não gera preventivas (backlog default vira OS)
  useEffect(() => {
    if (!atendimentoSelecionado?.id) return;
    const avulso = isContratoAvulso(atendimentoSelecionado.contratoTipo);
    setBacklogTipo(avulso ? "OS" : "PREVENTIVA");
  }, [atendimentoSelecionado?.id]);

  useEffect(() => {
    const carregarSetoresEEquipamentos = async () => {
      if (!scope?.grupoId || !atendimentoSelecionado?.clienteId) {
        setEquipamentos([]);
        setSetores([]);
        setEquipamentosCursor(null);
        setEquipamentosHasMore(false);
        return;
      }

      setLoadingEquipamentos(true);
      try {
        // Setores sempre carregam (usado como filtro tanto p/ Preventivas quanto p/ OS)
        const setoresSnap = await getDocs(
          query(
            collection(db, "setores"),
            where("grupoId", "==", scope.grupoId),
            where("empresaId", "==", atendimentoSelecionado.clienteId)
          )
        );
        const setoresLista = setoresSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setSetores(setoresLista);

        // Mantém carregamento de equipamentos apenas para lookup (label/sector), não como backlog.
        // (O backlog de preventivas agora lista instâncias da coleção "preventivas").
        const maquinasSnap = await getDocs(
          query(
            collection(db, "maquinas"),
            where("grupoId", "==", scope.grupoId),
            where("empresaId", "==", atendimentoSelecionado.clienteId),
            limit(EQUIPAMENTOS_PAGE_SIZE)
          )
        );
        const lista = maquinasSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setEquipamentos(lista);
        setEquipamentosCursor(maquinasSnap.docs[maquinasSnap.docs.length - 1] || null);
        setEquipamentosHasMore(maquinasSnap.docs.length >= EQUIPAMENTOS_PAGE_SIZE);
      } catch (e) {
        console.error("Erro ao carregar setores/equipamentos:", e);
        setEquipamentos([]);
        setSetores([]);
        setEquipamentosCursor(null);
        setEquipamentosHasMore(false);
      } finally {
        setLoadingEquipamentos(false);
      }
    };

    carregarSetoresEEquipamentos();
  }, [scope?.grupoId, atendimentoSelecionado?.clienteId, backlogTipo, atendimentoSelecionado?.contratoTipo]);

  const carregarMaisEquipamentos = async () => {
    if (backlogTipo === "OS" || backlogTipo === "PREVENTIVA") return;
    if (atendimentoSelecionado && isContratoAvulso(atendimentoSelecionado.contratoTipo)) return;
    if (!scope?.grupoId || !atendimentoSelecionado?.clienteId || !equipamentosHasMore) return;
    if (!equipamentosCursor) return;

    setLoadingEquipamentosMore(true);
    try {
      const snap = await getDocs(
        query(
          collection(db, "maquinas"),
          where("grupoId", "==", scope.grupoId),
          where("empresaId", "==", atendimentoSelecionado.clienteId),
          startAfter(equipamentosCursor),
          limit(EQUIPAMENTOS_PAGE_SIZE)
        )
      );

      const lista = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setEquipamentos((prev) => {
        const map = new Map(prev.map((item) => [item.id, item]));
        lista.forEach((item) => map.set(item.id, item));
        return Array.from(map.values());
      });
      setEquipamentosCursor(snap.docs[snap.docs.length - 1] || equipamentosCursor);
      setEquipamentosHasMore(snap.docs.length >= EQUIPAMENTOS_PAGE_SIZE);
    } catch (e) {
      console.error("Erro ao carregar mais equipamentos:", e);
    } finally {
      setLoadingEquipamentosMore(false);
    }
  };

  useEffect(() => {
    const carregarPreventivasCliente = async () => {
      if (backlogTipo !== "PREVENTIVA") {
        setPreventivas([]);
        return;
      }
      if (!scope?.grupoId || !atendimentoSelecionado?.clienteId) {
        setPreventivas([]);
        return;
      }
      if (atendimentoSelecionado && isContratoAvulso(atendimentoSelecionado.contratoTipo)) {
        setPreventivas([]);
        return;
      }

      setLoadingPreventivas(true);
      try {
        const [snap, snapMaquinas] = await Promise.all([
          getDocs(
            query(
              collection(db, "preventivas"),
              where("grupoId", "==", scope.grupoId),
              where("empresaId", "==", atendimentoSelecionado.clienteId),
              limit(700)
            )
          ),
          getDocs(
            query(
              collection(db, "maquinas"),
              where("grupoId", "==", scope.grupoId),
              where("empresaId", "==", atendimentoSelecionado.clienteId)
            )
          ),
        ]);

        let lista = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        lista = lista.filter((p) => p?.ativo !== false);

        const maquinasIds = new Set(snapMaquinas.docs.map((d) => String(d.id)));
        lista = lista.filter((p) => {
          const maquinaId = p?.maquinaId ? String(p.maquinaId) : "";
          if (!maquinaId) return true;
          return maquinasIds.has(maquinaId);
        });
        
        lista.sort((a, b) => {
          const da = a.proximaData?.toDate ? a.proximaData.toDate() : (a.proximaData ? new Date(a.proximaData) : null);
          const dbv = b.proximaData?.toDate ? b.proximaData.toDate() : (b.proximaData ? new Date(b.proximaData) : null);
          if (!da && !dbv) return 0;
          if (!da) return 1;
          if (!dbv) return -1;
          return da.getTime() - dbv.getTime();
        });

        // Remove preventivas ja agendadas em outras sessoes
        try {
          const qItensPrev = query(
            collection(db, "atendimentos_sessao_itens"),
            where("grupoId", "==", scope.grupoId),
            where("tipo", "==", "PREVENTIVA"),
            where("atendimentoId", "==", atendimentoSelecionado.id)
          );

          const snapItensPrev = await getDocs(qItensPrev);
          const preventivasAgendadas = new Set(
            snapItensPrev.docs.map((d) => d.data()?.preventivaId).filter(Boolean).map((id) => String(id))
          );
          lista = lista.filter((prev) => !preventivasAgendadas.has(String(prev.id)));
        } catch (e) {
          console.warn("Erro ao filtrar preventivas agendadas:", e);
        }

        setPreventivas(lista);
      } catch (e) {
        console.error("Erro ao carregar preventivas do cliente:", e);
        setPreventivas([]);
      } finally {
        setLoadingPreventivas(false);
      }
    };

    carregarPreventivasCliente();
  }, [scope?.grupoId, atendimentoSelecionado?.clienteId, backlogTipo, atendimentoSelecionado?.contratoTipo]);

  useEffect(() => {
    const carregarOsAbertas = async () => {
      if (backlogTipo !== "OS") {
        setOsAbertas([]);
        return;
      }
      if (!scope?.grupoId || !atendimentoSelecionado?.clienteId) {
        setOsAbertas([]);
        return;
      }

      setLoadingOs(true);
      try {
        const snap = await getDocs(
          query(
            collection(db, "ordensServico"),
            where("grupoId", "==", scope.grupoId),
            where("empresaId", "==", atendimentoSelecionado.clienteId),
            limit(500)
          )
        );

        let lista = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        // Mantém apenas "abertas" (filtra Finalizada/Cancelada)
        lista = lista.filter((os) => {
          const st = normStr(os?.statusNome || os?.status || "");
          if (st.includes("final")) return false;
          if (st.includes("cancel")) return false;
          return true;
        });

        // Ordena: Criticidade alta primeiro, depois mais antigas
        const critRank = (c) => {
          const v = normStr(c);
          if (v.includes("alta")) return 3;
          if (v.includes("media") || v.includes("média")) return 2;
          if (v.includes("baixa")) return 1;
          return 0;
        };
        lista.sort((a, b) => {
          const ra = critRank(a.criticidade);
          const rb = critRank(b.criticidade);
          if (rb !== ra) return rb - ra;
          const da = a.createdAt?.toDate ? a.createdAt.toDate() : (a.createdAt ? new Date(a.createdAt) : null);
          const db = b.createdAt?.toDate ? b.createdAt.toDate() : (b.createdAt ? new Date(b.createdAt) : null);
          if (!da && !db) return 0;
          if (!da) return 1;
          if (!db) return -1;
          return da.getTime() - db.getTime();
        });

        // Remove OS ja agendadas em outras sessoes
        try {
          const qItensOs = query(
            collection(db, "atendimentos_sessao_itens"),
            where("grupoId", "==", scope.grupoId),
            where("tipo", "==", "OS"),
            where("clienteId", "==", atendimentoSelecionado.clienteId)
          );

          const snapItensOs = await getDocs(qItensOs);
          const osAgendadas = new Set(
            snapItensOs.docs.map((d) => d.data()?.osId).filter(Boolean).map((id) => String(id))
          );
          lista = lista.filter((os) => !osAgendadas.has(String(os.id)));
        } catch (e) {
          console.warn("Erro ao filtrar OS agendadas:", e);
        }

        setOsAbertas(lista);
      } catch (e) {
        console.error("Erro ao carregar OS do cliente:", e);
        setOsAbertas([]);
      } finally {
        setLoadingOs(false);
      }
    };

    carregarOsAbertas();
  }, [scope?.grupoId, atendimentoSelecionado?.clienteId, backlogTipo]);

  useEffect(() => {
    const carregarItensSessao = async () => {
      if (!sessaoSelecionada?.id) {
        setSessaoItens([]);
        return;
      }
      if (!scope?.grupoId || !empresaIdForOps) return;

      setLoadingSessaoItens(true);
      try {
        const lista = await listarItensSessao(sessaoSelecionada.id, { empresaId: empresaIdForOps });
        let listaFiltrada = Array.isArray(lista) ? [...lista] : [];

        const itensPreventiva = listaFiltrada.filter((item) => {
          const tipo = String(item?.tipo || "").toUpperCase();
          return (tipo === "PREVENTIVA" || item?.preventivaId) && !!item?.preventivaId;
        });

        if (itensPreventiva.length > 0 && scope?.grupoId && atendimentoSelecionado?.clienteId) {
          const [snapPrev, snapMaquinas] = await Promise.all([
            getDocs(
              query(
                collection(db, "preventivas"),
                where("grupoId", "==", scope.grupoId),
                where("empresaId", "==", atendimentoSelecionado.clienteId),
                limit(700)
              )
            ),
            getDocs(
              query(
                collection(db, "maquinas"),
                where("grupoId", "==", scope.grupoId),
                where("empresaId", "==", atendimentoSelecionado.clienteId)
              )
            ),
          ]);

          const maquinasIds = new Set(snapMaquinas.docs.map((d) => String(d.id)));
          const preventivasValidasIds = new Set(
            snapPrev.docs
              .map((d) => ({ id: d.id, ...d.data() }))
              .filter((p) => p?.ativo !== false)
              .filter((p) => {
                const maquinaId = p?.maquinaId ? String(p.maquinaId) : "";
                if (!maquinaId) return true;
                return maquinasIds.has(maquinaId);
              })
              .map((p) => String(p.id))
          );

          const itensOrfaosIds = itensPreventiva
            .filter((item) => !preventivasValidasIds.has(String(item.preventivaId)))
            .map((item) => item.id)
            .filter(Boolean);

          if (itensOrfaosIds.length > 0) {
            await removerItensSessao(itensOrfaosIds);
            listaFiltrada = listaFiltrada.filter((item) => !itensOrfaosIds.includes(item.id));

            const sessaoId = sessaoSelecionada.id;
            setSessaoItensCount((prev) => ({
              ...prev,
              [sessaoId]: Math.max(0, (prev[sessaoId] ?? lista.length) - itensOrfaosIds.length),
            }));
            setVisitas((prev) => prev.map((v) => (
              v.id === sessaoId
                ? { ...v, itensCount: Math.max(0, (v.itensCount ?? lista.length) - itensOrfaosIds.length) }
                : v
            )));
          }
        }

        setSessaoItens(listaFiltrada);
      } catch (e) {
        console.error("Erro ao carregar itens da sessao:", e);
        setSessaoItens([]);
      } finally {
        setLoadingSessaoItens(false);
      }
    };

    carregarItensSessao();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessaoSelecionada?.id, scope?.grupoId, empresaIdForOps, atendimentoSelecionado?.clienteId, removerItensSessao]);

  const sessaoItensRefMap = useMemo(() => {
    const mapEquip = {};
    const mapOs = {};
    const mapPrev = {};
    sessaoItens.forEach((item) => {
      const tipo = String(item.tipo || "").toUpperCase();
      if (tipo === "OS" || item.osId) {
        mapOs[String(item.osId)] = item;
      } else if (tipo === "PREVENTIVA" || item.preventivaId) {
        mapPrev[String(item.preventivaId)] = item;
      } else {
        mapEquip[String(item.equipamentoId)] = item;
      }
    });
    return { mapEquip, mapOs, mapPrev };
  }, [sessaoItens]);

  const equipamentosPendentes = useMemo(() => {
    return equipamentos.filter((eq) => !sessaoItensRefMap.mapEquip[String(eq.id)]);
  }, [equipamentos, sessaoItensRefMap]);

  const preventivasPendentes = useMemo(() => {
    return preventivas.filter((p) => !sessaoItensRefMap.mapPrev[String(p.id)]);
  }, [preventivas, sessaoItensRefMap]);

  const osPendentes = useMemo(() => {
    return osAbertas.filter((os) => !sessaoItensRefMap.mapOs[String(os.id)]);
  }, [osAbertas, sessaoItensRefMap]);

  const itensSessaoDetalhados = useMemo(() => {
    return sessaoItens.map((item) => {
      const tipo = String(item.tipo || "").toUpperCase();
      const isOs = tipo === "OS" || !!item.osId;

      if (tipo === "PREVENTIVA" || item.preventivaId) {
        const codigo = item.preventivaCodigo || item.codigo || "";
        const nomePrev = item.preventivaNome || item.nome || "Preventiva";
        const maq = item.maquinaNome || "";
        const label = codigo ? `${codigo} - ${nomePrev}` : nomePrev;
        return {
          id: item.id,
          tipo: "PREVENTIVA",
          preventivaId: item.preventivaId,
          setorId: item.setorId || null,
          label,
          subLabel: [maq, item.setorNome || ""].filter(Boolean).join(" • "),
        };
      }

      if (isOs) {
        const titulo = item.osTitulo || "";
        const codigo = item.osCodigo || "";
        const label = codigo ? `#${codigo}${titulo ? ` - ${titulo}` : ""}` : (titulo || item.osId);
        return {
          id: item.id,
          tipo: "OS",
          osId: item.osId,
          setorId: item.setorId || null,
          label,
          subLabel: item.setorNome || "",
        };
      }

      const equipamento = equipamentosById[String(item.equipamentoId)] || {};
      return {
        id: item.id,
        tipo: "EQUIPAMENTO",
        equipamentoId: item.equipamentoId,
        setorId: item.setorId || equipamento.setorId || null,
        label: item.equipamentoNome || equipamento.nome || item.equipamentoId,
        subLabel: item.setorNome || equipamento.setorNome || setoresById[item.setorId]?.nome || "",
      };
    });
  }, [sessaoItens, equipamentosById, setoresById]);
  const preventivasPendentesDetalhadas = useMemo(() => {
    return preventivasPendentes.map((p) => {
      const dt = p.proximaData?.toDate ? p.proximaData.toDate() : (p.proximaData ? new Date(p.proximaData) : null);
      const dataLabel = dt && !Number.isNaN(dt.getTime())
        ? dt.toLocaleDateString("pt-BR")
        : "sem data";

      const codigo = p.codigo || p.modeloPreventivaCodigo || "";
      const nomePrev = p.nome || "Preventiva";
      const label = codigo ? `${codigo} - ${nomePrev}` : nomePrev;
      const subLabel = [p.maquinaNome || "", `Próx: ${dataLabel}`].filter(Boolean).join(" • ");
      return {
        id: p.id,
        tipo: "PREVENTIVA",
        preventivaId: p.id,
        preventivaCodigo: codigo || null,
        preventivaNome: nomePrev || null,
        maquinaId: p.maquinaId || null,
        maquinaNome: p.maquinaNome || "",
        periodicidade: p.periodicidade || null,
        setorId: p.setorId || null,
        setorNome: p.setorNome || setoresById[p.setorId]?.nome || "",
        label,
        subLabel,
      };
    });
  }, [preventivasPendentes, setoresById]);


  const equipamentosPendentesDetalhados = useMemo(() => {
    return equipamentosPendentes.map((eq) => ({
      id: eq.id,
      tipo: "EQUIPAMENTO",
      equipamentoId: eq.id,
      setorId: eq.setorId || null,
      equipamentoNome: eq.nome || "",
      setorNome: eq.setorNome || setoresById[eq.setorId]?.nome || "",
      label: eq.nome || eq.codigo || eq.id,
      subLabel: eq.setorNome || setoresById[eq.setorId]?.nome || "",
    }));
  }, [equipamentosPendentes, setoresById]);

  const osPendentesDetalhados = useMemo(() => {
    return osPendentes.map((os) => ({
      id: os.id,
      tipo: "OS",
      osId: os.id,
      osCodigo: os.codigo || null,
      osTitulo: os.titulo || os.defeitoNome || os.descricao || null,
      setorId: os.setorId || null,
      setorNome: os.setorNome || setoresById[os.setorId]?.nome || "",
      label: os.codigo ? `#${os.codigo} - ${os.titulo || os.defeitoNome || ""}` : (os.titulo || os.id),
      subLabel: [os.setorNome || setoresById[os.setorId]?.nome || "", os.statusNome || ""].filter(Boolean).join(" • "),
    }));
  }, [osPendentes, setoresById]);

  const setoresOptions = useMemo(() => {
    return setores.map((s) => ({ value: s.id, label: s.nome || s.codigo || s.id }));
  }, [setores]);

  const sessaoOptions = useMemo(() => {
    return sessoes.map((sess) => {
      const tecnicoNome = getTecnicoNome(sess.tecnicoId);
      const label = `Sessao em ${formatDateTime(sess.inicioEm)} • ${tecnicoNome}`;
      return { value: sess.id, label };
    });
  }, [sessoes, tecnicosByRef]);

  const handleSelecionarSessao = (sessao) => {
    setSessaoSelecionada(sessao);
  };

  const handleSelecionarSessaoPorId = (sessaoId) => {
    if (!sessaoId) {
      setSessaoSelecionada(null);
      return;
    }
    const sessao = sessoes.find((s) => s.id === sessaoId) || null;
    setSessaoSelecionada(sessao);
  };

  const mapItemSessaoExpandido = (item) => {
    const tipoRaw = String(item?.tipo || "").toUpperCase();
    if (tipoRaw === "PREVENTIVA" || item?.preventivaId) {
      const codigo = item?.preventivaCodigo || item?.codigo || "";
      const nome = item?.preventivaNome || item?.nome || "Preventiva";
      return {
        id: item?.id || `prev-${item?.preventivaId || Math.random()}`,
        tipo: "PREVENTIVA",
        label: codigo ? `${codigo} - ${nome}` : nome,
        subLabel: [item?.maquinaNome || "", item?.setorNome || ""].filter(Boolean).join(" • "),
      };
    }

    if (tipoRaw === "OS" || item?.osId) {
      const codigo = item?.osCodigo || item?.codigo || "";
      const titulo = item?.osTitulo || item?.titulo || item?.descricao || item?.osId || "OS";
      return {
        id: item?.id || `os-${item?.osId || Math.random()}`,
        tipo: "OS",
        label: codigo ? `#${codigo} - ${titulo}` : titulo,
        subLabel: item?.setorNome || "",
      };
    }

    if (tipoRaw === "INSTALACAO" || item?.checklistInstalacaoId || item?.propostaInstalacaoId) {
      const nome = item?.propostaInstalacaoNome
        || item?.checklistInstalacaoNome
        || item?.equipamentoNome
        || "Instalação";
      return {
        id: item?.id || `inst-${item?.propostaInstalacaoId || item?.equipamentoId || Math.random()}`,
        tipo: "INSTALACAO",
        label: `Instalação • ${nome}`,
        subLabel: item?.setorNome || "",
      };
    }

    return {
      id: item?.id || `eq-${item?.equipamentoId || Math.random()}`,
      tipo: "EQUIPAMENTO",
      label: item?.equipamentoNome || item?.nome || item?.equipamentoId || "Equipamento",
      subLabel: item?.setorNome || "",
    };
  };

  const handleToggleExpandirSessao = async (sessao) => {
    if (!sessao?.id) return;
    if (sessaoExpandidaId === sessao.id) {
      setSessaoExpandidaId(null);
      return;
    }

    setSessaoExpandidaId(sessao.id);
    setSessaoSelecionada(sessao);

    if (sessaoItensExpandidaMap[sessao.id]) return;

    setLoadingSessaoExpandidaId(sessao.id);
    try {
      const lista = await listarItensSessao(sessao.id, { empresaId: empresaIdForOps });
      const itensDetalhados = (lista || []).map(mapItemSessaoExpandido);
      setSessaoItensExpandidaMap((prev) => ({ ...prev, [sessao.id]: itensDetalhados }));
    } catch (e) {
      console.error("Erro ao carregar itens da sessão expandida:", e);
      setSessaoItensExpandidaMap((prev) => ({ ...prev, [sessao.id]: [] }));
    } finally {
      setLoadingSessaoExpandidaId("");
    }
  };

  const handleAdicionarItensSessao = async (items) => {
    if (!sessaoSelecionada?.id || !atendimentoSelecionado?.id) return;
    setSalvandoSessaoItens(true);
    setErro("");
    try {
      const competenciaAtual = atendimentoSelecionado?.competencia || null;
      const itemsEnriquecidos = (items || []).map((it) => {
        if (!competenciaAtual) return it;

        const competencia = it?.competencia || competenciaAtual;
        const competenciaMes = it?.competenciaMes || String(competencia).slice(0, 7);
        const quinzena = it?.quinzena || (String(competencia).includes("-Q2") ? "Q2" : (String(competencia).includes("-Q1") ? "Q1" : null));

        const preventivaId = it?.preventivaId || it?.id;
        const isPrev = String(it?.tipo || "").toUpperCase() === "PREVENTIVA" || !!it?.preventivaId;
        const slotKey = it?.slotKey || (isPrev && preventivaId && competencia ? `${preventivaId}|${competencia}` : null);

        return {
          ...it,
          competencia,
          competenciaMes,
          quinzena,
          slotKey,
        };
      });

      const tipoAtual = obterTipoVisita(sessaoItens);
      const tipoNovo = obterTipoVisita(itemsEnriquecidos);
      if (tipoAtual && tipoNovo && tipoAtual !== tipoNovo) {
        setErro("Não é permitido misturar tipos diferentes na mesma visita.");
        return;
      }

      await adicionarItensSessao(
        atendimentoSelecionado.id,
        sessaoSelecionada.id,
        atendimentoSelecionado.clienteId,
        itemsEnriquecidos
      );
      const lista = await listarItensSessao(sessaoSelecionada.id, { empresaId: empresaIdForOps });
      setSessaoItens(lista);
      setSessaoItensCount((prev) => ({
        ...prev,
        [sessaoSelecionada.id]: lista.length,
      }));
    } catch (e) {
      console.error("Erro ao adicionar itens da sessao:", e);
      setErro("Erro ao adicionar itens na sessao.");
    } finally {
      setSalvandoSessaoItens(false);
    }
  };

  const handleRemoverItensSessao = async (items) => {
    if (!sessaoSelecionada?.id) return;
    setSalvandoSessaoItens(true);
    setErro("");
    try {
      await removerItensSessao(items.map((item) => item.id));
      const lista = await listarItensSessao(sessaoSelecionada.id, { empresaId: empresaIdForOps });
      setSessaoItens(lista);
      setSessaoItensCount((prev) => ({
        ...prev,
        [sessaoSelecionada.id]: lista.length,
      }));
    } catch (e) {
      console.error("Erro ao remover itens da sessao:", e);
      setErro("Erro ao remover itens da sessao.");
    } finally {
      setSalvandoSessaoItens(false);
    }
  };
  const handleConcluirAtendimento = async () => {
    if (!atendimentoSelecionado?.id) return;
    
    const confirmar = window.confirm(
      "Deseja marcar este atendimento como CONCLUÍDO? Certifique-se de que todas as sessões foram realizadas."
    );
    if (!confirmar) return;

    setConcluindoAtendimento(true);
    setErro("");
    try {
      // Verificar se há OS de instalação associadas a este atendimento
      let temInstalacao = false;
      try {
        const osDoAtendimentoQuery = query(
          collection(db, "ordensServico"),
          where("grupoId", "==", scope.grupoId),
          where("empresaId", "==", atendimentoSelecionado.clienteId)
        );
        const osSnap = await getDocs(osDoAtendimentoQuery);
        temInstalacao = osSnap.docs.some((osDoc) => {
          const osData = osDoc.data();
          const tipoServico = String(osData?.tipoServico || "").toLowerCase();
          return tipoServico.includes("instal");
        });
      } catch (errOs) {
        console.warn("Erro ao verificar OS de instalação:", errOs);
      }

      const contratoTipo = String(atendimentoSelecionado.contratoTipo || "").trim();
      const isAvulso = String(contratoTipo).toLowerCase().includes("avulso");
      const deveAbrirPreventivasAposInstalacao = temInstalacao && !isAvulso;

      const atendimentoRef = doc(db, "atendimentos_preventiva", atendimentoSelecionado.id);
      await updateDoc(atendimentoRef, {
        status: "CONCLUIDO",
        updatedAt: serverTimestamp(),
      });

      // Se foi uma instalação com contrato, abrir preventivas para próxima competência
      if (deveAbrirPreventivasAposInstalacao && scope?.grupoId) {
        try {
          const snapContratos = await getDocs(
            query(
              collection(db, "financeiro_contratos"),
              where("grupoId", "==", scope.grupoId),
              where("clienteId", "==", atendimentoSelecionado.clienteId),
              where("status", "==", "APROVADO")
            )
          );

          const contratosAprovados = snapContratos.docs.map((d) => ({ id: d.id, ...d.data() }));
          const contratoMatchTipo = contratosAprovados.find((c) => {
            const tipoC = String(c?.contratoTipo || c?.setorContratoTipo || "").trim().toUpperCase();
            return tipoC === String(contratoTipo || "").trim().toUpperCase();
          });

          const contratoRef = contratoMatchTipo || contratosAprovados[0] || null;
          if (contratoRef) {
            await sincronizarPlanejamentoContratoAprovado({
              grupoId: scope.grupoId,
              clienteId: atendimentoSelecionado.clienteId,
              clienteNome: clientesById[atendimentoSelecionado.clienteId]?.nome || "",
              empresaId: atendimentoSelecionado.clienteId,
              contrato: contratoRef,
              criadoPor: usuario?.uid || usuario?.id || "",
              aprovadoEm: contratoRef?.aprovadoEm || contratoRef?.updatedAt || new Date(),
            });
          }
        } catch (errPrev) {
          console.warn("Aviso: Falha ao abrir preventivas após instalação:", errPrev);
          // Não falha o atendimento se as preventivas não forem criadas
        }
      }

      setAtendimentoSelecionado((prev) => prev ? { ...prev, status: "CONCLUIDO" } : null);
      setAtendimentos((prev) => prev.map((a) => 
        a.id === atendimentoSelecionado.id ? { ...a, status: "CONCLUIDO" } : a
      ));
      setSucesso("Atendimento marcado como CONCLUÍDO com sucesso!");
    } catch (e) {
      console.error("Erro ao concluir atendimento:", e);
      setErro(`Erro ao concluir atendimento: ${e.message || "Tente novamente"}`);
    } finally {
      setConcluindoAtendimento(false);
    }
  };

  const handleGerarRelatorio = async () => {
    if (!atendimentoSelecionado) return;

    setGerandoRelatorio(true);
    setErro("");

    try {
      const cliente = clientesById[atendimentoSelecionado.clienteId];
      const clienteNome = cliente?.nome || cliente?.razaoSocial || "Cliente";

      const hoje = new Date();
      const progresso = atendimentoSelecionado?.progresso;
      const progressoTexto = progresso?.equipamentosConcluidos != null
        ? `${progresso.equipamentosConcluidos}/${progresso.equipamentosAlvo || 0}`
        : "-";

      const sessoesRows = sessoes.length
        ? sessoes.map((s) => {
          const tecnicoNome = getTecnicoNome(s.tecnicoId);
          return `
            <tr>
              <td>${escapeHtml(formatDateTime(s.inicioEm))}</td>
              <td>${escapeHtml(formatDateTime(s.fimEm))}</td>
              <td>${escapeHtml(tecnicoNome)}</td>
              <td>${escapeHtml(s.status || "PLANEJADA")}</td>
              <td>${escapeHtml(`${sessaoItensCount[s.id] ?? 0} itens`)}</td>
            </tr>
          `;
        }).join("")
        : `<tr><td colspan="5" style="text-align:center;color:#9ca3af;">Nenhuma sessão cadastrada.</td></tr>`;

      const itensRows = sessaoSelecionada && itensSessaoDetalhados.length > 0
        ? itensSessaoDetalhados.map((item) => `
            <tr>
              <td>${escapeHtml(item.tipo || "-")}</td>
              <td>${escapeHtml(item.label || "-")}</td>
              <td>${escapeHtml(item.subLabel || "-")}</td>
            </tr>
          `).join("")
        : `<tr><td colspan="3" style="text-align:center;color:#9ca3af;">Nenhum item na sessão selecionada.</td></tr>`;

      const sessaoBase = sessaoSelecionada || sessoes[0] || null;
      const parseDate = (value) => {
        if (!value) return null;
        const dt = typeof value?.toDate === "function" ? value.toDate() : new Date(value);
        return Number.isNaN(dt.getTime()) ? null : dt;
      };
      const dtInicio = parseDate(sessaoBase?.inicioEm);
      const dtFim = parseDate(sessaoBase?.fimEm);
      const dataInicioISO = dtInicio ? `${dtInicio.getFullYear()}-${String(dtInicio.getMonth() + 1).padStart(2, "0")}-${String(dtInicio.getDate()).padStart(2, "0")}` : "";
      const dataFimISO = dtFim ? `${dtFim.getFullYear()}-${String(dtFim.getMonth() + 1).padStart(2, "0")}-${String(dtFim.getDate()).padStart(2, "0")}` : "";
      const horaInicio = dtInicio ? `${String(dtInicio.getHours()).padStart(2, "0")}:${String(dtInicio.getMinutes()).padStart(2, "0")}` : "";
      const horaFim = dtFim ? `${String(dtFim.getHours()).padStart(2, "0")}:${String(dtFim.getMinutes()).padStart(2, "0")}` : "";
      let totalHoras = "0,00 h";
      if (dtInicio && dtFim) {
        const diffMs = Math.max(0, dtFim.getTime() - dtInicio.getTime());
        totalHoras = `${(diffMs / 3600000).toFixed(2).replace(".", ",")} h`;
      }

      const academiaNome =
        academias.find((a) => String(a.id) === String(academiaId))?.nome ||
        sessaoBase?.setorNome ||
        itensSessaoDetalhados[0]?.subLabel ||
        "-";

      const servicosExecutados = itensSessaoDetalhados.length
        ? itensSessaoDetalhados.map((item) => `${item.tipo}: ${item.label}`).join("\n")
        : "-";

      const defeitosTexto = osPendentesDetalhados.length
        ? osPendentesDetalhados.map((os) => os.label).join("\n")
        : "-";

      const resumoTexto = `TIPO DE RELATÓRIO: PLANEJAMENTO DE VISITAS

CLIENTE: ${clienteNome || "-"}
COMPETÊNCIA: ${atendimentoSelecionado.competencia || "-"}
CONTRATO: ${atendimentoSelecionado.contratoTipo || "-"}
STATUS: ${atendimentoSelecionado.status || "ABERTO"}

PROGRESSO: ${progressoTexto}

SESSÕES CADASTRADAS: ${sessoes.length}
SESSÃO SELECIONADA: ${sessaoSelecionada ? formatDateTime(sessaoSelecionada.inicioEm) : "-"}
ITENS NA SESSÃO SELECIONADA: ${itensSessaoDetalhados.length}

Gerado em: ${hoje.toLocaleString("pt-BR")}`;

      let html = `<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>${escapeHtml(`Relatório de Atendimento - ${clienteNome}`)}</title>
  <style>
    :root {
      --cor-primaria: #0f172a;
      --cor-secundaria: #1e293b;
      --cor-fundo: #020617;
      --cor-cartao: #0b1220;
      --cor-borda: rgba(148,163,184,0.3);
      --cor-texto: #e5e7eb;
      --cor-texto-fraco: #94a3b8;
      --radius: 10px;
      --shadow: 0 10px 25px rgba(0,0,0,0.4);
    }

    * {
      box-sizing: border-box;
      font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    }
    body {
      margin: 0;
      padding: 16px;
      background: radial-gradient(circle at top left, #0b1220, #020617 60%, #01030a);
      color: var(--cor-texto);
    }

    .app-container { max-width: 1100px; margin: 0 auto; }

    .header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 12px;
      margin-bottom: 20px;
      padding: 14px 18px;
      border-radius: var(--radius);
      background: linear-gradient(135deg, var(--cor-primaria), var(--cor-secundaria));
      border: 1px solid rgba(148,163,184,0.4);
      box-shadow: var(--shadow);
    }

    .logo-area {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .logo-titulo {
      font-size: 1.3rem;
      font-weight: 700;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: #f97316;
    }

    .logo-sub { font-size: 0.8rem; color: var(--cor-texto-fraco); }

    .tag-versao {
      font-size: 0.7rem;
      padding: 4px 8px;
      border-radius: 999px;
      background: rgba(15,118,110,0.15);
      color: #86efac;
      border: 1px solid rgba(34,197,94,0.4);
      text-transform: uppercase;
      letter-spacing: 0.08em;
    }

    .main-card {
      background: var(--cor-cartao);
      border-radius: var(--radius);
      box-shadow: var(--shadow);
      border: 1px solid var(--cor-borda);
      padding: 18px;
      display: grid;
      gap: 16px;
    }

    .grid {
      display: grid;
      gap: 12px;
    }

    @media (min-width: 900px) {
      .grid-2 {
        grid-template-columns: 1.2fr 1fr;
      }
    }

    .secao {
      border-radius: var(--radius);
      border: 1px solid var(--cor-borda);
      padding: 12px 12px 10px;
      background: linear-gradient(180deg, rgba(15,23,42,0.42), rgba(2,6,23,0.6));
      box-shadow: 0 0 0 1px rgba(30,41,59,0.55);
    }

    .secao-titulo {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 10px;
      font-size: 0.78rem;
      font-weight: 700;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: #e2e8f0;
    }

    .badge {
      font-size: 0.7rem;
      padding: 4px 8px;
      border-radius: 999px;
      background: rgba(14,165,233,0.16);
      color: #bae6fd;
      border: 1px solid rgba(56,189,248,0.38);
    }

    .linha {
      display: grid;
      gap: 10px;
      margin-bottom: 8px;
    }

    @media (min-width: 820px) {
      .linha-2 { grid-template-columns: repeat(2, 1fr); }
      .linha-4 { grid-template-columns: repeat(4, 1fr); }
    }

    .label { font-size: 0.72rem; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.06em; }
    .valor { font-size: 0.95rem; color: #f8fafc; font-weight: 600; }

    table {
      width: 100%;
      border-collapse: collapse;
      margin-top: 8px;
      font-size: 0.85rem;
    }

    thead th {
      text-align: left;
      padding: 8px;
      color: #cbd5e1;
      border-bottom: 1px solid rgba(148,163,184,0.25);
      background: rgba(15,23,42,0.45);
      text-transform: uppercase;
      font-size: 0.72rem;
      letter-spacing: 0.06em;
    }

    tbody td {
      padding: 8px;
      border-bottom: 1px solid rgba(148,163,184,0.14);
      color: #e2e8f0;
    }

    tbody tr:last-child td { border-bottom: none; }

    .resumo {
      font-size: 0.8rem;
      background: rgba(15,23,42,0.42);
      border-radius: 8px;
      padding: 8px;
      border: 1px dashed rgba(148,163,184,0.35);
      max-height: 190px;
      overflow: auto;
      white-space: pre-wrap;
      color: #dbeafe;
    }

    .footer {
      margin-top: 12px;
      font-size: 0.7rem;
      text-align: right;
      color: #94a3b8;
    }

    @media print {
      body { background: #fff; color: #111827; padding: 8px; }
      .main-card, .secao { background: #fff; color: #111827; border-color: #d1d5db; box-shadow: none; }
      .header { background: #fff; color: #111827; border-color: #d1d5db; box-shadow: none; }
      .logo-sub, .footer, .label { color: #4b5563; }
      .valor, tbody td, thead th, .secao-titulo { color: #111827; }
      thead th { background: #f3f4f6; }
      .resumo { background: #f8fafc; color: #111827; border-color: #cbd5e1; }
    }
  </style>
</head>
<body>
  <div class="app-container">
    <header class="header">
      <div class="logo-area">
        <div class="logo-titulo">Ramalho ProFit</div>
        <div class="logo-sub">Ficha de Atendimento Técnico • Montagem & Manutenção Fitness</div>
      </div>
      <div><div class="tag-versao">App Técnico • v0.7 PDF Pro+</div></div>
    </header>

    <main class="main-card">
      <div class="grid grid-2">
        <div class="grid">
          <section class="secao">
            <div class="secao-titulo">
              <span>DADOS DO ATENDIMENTO</span>
              <div class="badge">OS digital</div>
            </div>

            <div class="linha linha-3">
              <div>
                <label>Cliente</label>
                <input type="text" value="${escapeHtml(clienteNome)}" readonly>
              </div>
              <div>
                <label>Academia / Unidade</label>
                <input type="text" value="${escapeHtml(academiaNome)}" readonly>
              </div>
              <div>
                <label>Equipamento</label>
                <input type="text" value="${escapeHtml(`${itensSessaoDetalhados.length} item(ns)`) }" readonly>
              </div>
            </div>

            <div class="linha linha-2" style="margin-top: 8px;">
              <div>
                <label>Responsável no local</label>
                <input type="text" value="${escapeHtml(getTecnicoNome(sessaoBase?.tecnicoId))}" readonly>
              </div>
              <div>
                <label>Tipo de relatório</label>
                <select disabled>
                  <option selected>Manutenção</option>
                  <option>Montagem</option>
                </select>
              </div>
            </div>

            <div class="linha linha-2" style="margin-top: 6px;">
              <div>
                <label>Data início</label>
                <input type="date" value="${escapeHtml(dataInicioISO)}" readonly>
              </div>
              <div>
                <label>Data final</label>
                <input type="date" value="${escapeHtml(dataFimISO)}" readonly>
              </div>
            </div>

            <div class="linha linha-2" style="margin-top: 6px;">
              <div>
                <label>Hora início</label>
                <input type="time" value="${escapeHtml(horaInicio)}" readonly>
              </div>
              <div>
                <label>Hora fim</label>
                <input type="time" value="${escapeHtml(horaFim)}" readonly>
              </div>
            </div>

            <div class="horas-box" style="margin-top: 8px;">
              <span>Total de horas trabalhadas:</span>
              <span class="horas-valor">${escapeHtml(totalHoras)}</span>
            </div>
          </section>

          <section class="secao">
            <div class="secao-titulo">
              <span>DIAGNÓSTICO • SERVIÇOS EXECUTADOS</span>
            </div>

            <div class="linha linha-2" style="margin-bottom: 6px;">
              <div>
                <label>Tipo de defeito principal</label>
                <input type="text" value="${escapeHtml(atendimentoSelecionado.status || "-")}" readonly>
              </div>
            </div>

            <div>
              <label>Defeitos encontrados</label>
              <textarea readonly>${escapeHtml(defeitosTexto)}</textarea>
            </div>

            <div style="margin-top: 6px;">
              <label>Serviços executados</label>
              <textarea readonly>${escapeHtml(servicosExecutados)}</textarea>
            </div>

            <div style="margin-top: 6px;">
              <label>Peças substituídas (códigos / quantidades)</label>
              <textarea readonly>${escapeHtml("-")}</textarea>
            </div>
          </section>

          <section class="secao" style="display:none;">
            <div class="secao-titulo">
              <span>MONTAGEM • EQUIPAMENTOS INSTALADOS</span>
              <div class="badge">Relatório de montagem</div>
            </div>
          </section>
        </div>

        <div class="grid">
          <section class="secao">
            <div class="secao-titulo">
              <span>GRAVIDADE • AVALIAÇÃO DA MÁQUINA</span>
            </div>

            <div class="linha linha-2">
              <div>
                <label>Gravidade da situação</label>
                <select disabled>
                  <option selected>Média</option>
                </select>
              </div>
              <div>
                <label>Avaliação geral (1 a 5)</label>
                <select disabled>
                  <option selected>3 - Regular</option>
                </select>
              </div>
            </div>

            <div style="margin-top: 10px;">
              <div class="gravidade-pill gravidade-media">
                Estado atual: <strong>Gravidade média</strong>
              </div>
            </div>

            <div style="margin-top: 8px;">
              <label>Recomendações ao cliente</label>
              <textarea readonly>${escapeHtml("-")}</textarea>
            </div>
          </section>

          <section class="secao">
            <div class="secao-titulo">
              <span>FINANCEIRO • DESLOCAMENTO</span>
            </div>

            <div class="linha linha-2">
              <div>
                <label>Km rodados</label>
                <input type="text" value="0" readonly>
              </div>
              <div>
                <label>Valor deslocamento (R$) • automático (km × 1,20)</label>
                <input type="text" class="valor-total" value="0,00" readonly>
              </div>
            </div>

            <div class="linha linha-2" style="margin-top: 6px;">
              <div>
                <label>Pedágio (R$)</label>
                <input type="text" value="0,00" readonly>
              </div>
              <div>
                <label>Valor mão de obra (R$)</label>
                <input type="text" value="0,00" readonly>
              </div>
            </div>

            <div class="linha linha-2" style="margin-top: 6px;">
              <div>
                <label>Valor peças (R$)</label>
                <input type="text" value="0,00" readonly>
              </div>
              <div>
                <label>Desconto (R$)</label>
                <input type="text" value="0,00" readonly>
              </div>
            </div>

            <div class="linha linha-2" style="margin-top: 6px;">
              <div>
                <label>Valor total (R$)</label>
                <input type="text" class="valor-total" value="0,00" readonly>
              </div>
            </div>
          </section>

          <section class="secao">
            <div class="secao-titulo">
              <span>FOTOS • ANTES / DEPOIS</span>
            </div>
            <div class="linha linha-2">
              <div>
                <label>Fotos ANTES</label>
                <textarea readonly>${escapeHtml("Nenhuma foto registrada.")}</textarea>
              </div>
              <div>
                <label>Fotos DEPOIS</label>
                <textarea readonly>${escapeHtml("Nenhuma foto registrada.")}</textarea>
              </div>
            </div>
          </section>

          <section class="secao">
            <div class="secao-titulo">
              <span>OBSERVAÇÕES • GARANTIA • RESUMO</span>
              <div class="badge">Pré-visualização</div>
            </div>
            <div style="margin-bottom: 6px;">
              <label>Observações gerais</label>
              <textarea readonly>${escapeHtml(sessaoBase?.observacoes || "-")}</textarea>
            </div>
            <div style="margin-bottom: 6px;">
              <label>Condições de garantia</label>
              <textarea readonly>${escapeHtml("Garantia de 30 dias sobre o serviço executado.")}</textarea>
            </div>
            <div style="margin-top: 6px;">
              <label>Responsável técnico Ramalho ProFit</label>
              <input type="text" value="${escapeHtml(getTecnicoNome(sessaoBase?.tecnicoId))}" readonly>
            </div>
            <div style="margin-top: 6px;">
              <label>Assinatura / Confirmação do cliente</label>
              <input type="text" value="-" readonly>
            </div>
            <div class="resumo" style="margin-top: 8px;">${escapeHtml(resumoTexto)}</div>
          </section>
        </div>
      </div>

      <div class="footer">
        Ramalho ProFit • App interno de atendimento técnico • Protótipo web (Replit)
      </div>
    </main>
  </div>
</body>
</html>`;

      const toDataBr = (iso) => {
        if (!iso) return "-";
        const [ano, mes, dia] = String(iso).split("-");
        if (!ano || !mes || !dia) return "-";
        return `${dia}/${mes}/${ano}`;
      };
      const escNl = (v) => escapeHtml(v || "-").replace(/\n/g, "<br>");

      const EMPRESA = {
        nome: String(relatorioEmpresaCfg?.empresaNome || RELATORIO_EMPRESA_DEFAULT.empresaNome),
        sub: String(relatorioEmpresaCfg?.empresaSubtitulo || RELATORIO_EMPRESA_DEFAULT.empresaSubtitulo),
        cnpj: String(relatorioEmpresaCfg?.empresaCnpj || RELATORIO_EMPRESA_DEFAULT.empresaCnpj),
        telefone: String(relatorioEmpresaCfg?.empresaTelefone || RELATORIO_EMPRESA_DEFAULT.empresaTelefone),
        instagram: String(relatorioEmpresaCfg?.empresaInstagram || RELATORIO_EMPRESA_DEFAULT.empresaInstagram),
        email: String(relatorioEmpresaCfg?.empresaEmail || RELATORIO_EMPRESA_DEFAULT.empresaEmail),
        site: String(relatorioEmpresaCfg?.empresaSite || RELATORIO_EMPRESA_DEFAULT.empresaSite),
        endereco: String(relatorioEmpresaCfg?.empresaEndereco || RELATORIO_EMPRESA_DEFAULT.empresaEndereco),
      };

      const tipoRelLabel = "Relatório de Manutenção";
      const dataInicioBR = toDataBr(dataInicioISO);
      const dataFimBR = toDataBr(dataFimISO);
      const tecnicoNome = getTecnicoNome(sessaoBase?.tecnicoId);
      const equipamentoDescricao = itensSessaoDetalhados[0]?.label || "-";
      const logoHtml = `<div style="font-size:18px;font-weight:800;letter-spacing:0.12em;text-transform:uppercase;color:#f97316;">${escapeHtml(EMPRESA.nome)}</div>`;

      const preventivaIdsSessao = Array.from(
        new Set(
          (sessaoItens || [])
            .map((item) => item?.preventivaId)
            .filter(Boolean)
            .map((id) => String(id))
        )
      );

      let fotosPreventivas = [];
      if (preventivaIdsSessao.length > 0 && scope?.grupoId) {
        const colecoes = await Promise.all(
          preventivaIdsSessao.map(async (preventivaId) => {
            try {
              const prevSnap = await getDoc(doc(db, "preventivas", preventivaId));
              const prev = prevSnap.exists() ? prevSnap.data() : null;

              const snapExec = await getDocs(
                query(
                  collection(db, "preventivasExecucoes"),
                  where("grupoId", "==", scope.grupoId),
                  where("preventivaId", "==", preventivaId),
                  limit(30)
                )
              );

              const execs = snapExec.docs
                .map((d) => ({ id: d.id, ...d.data() }))
                .sort((a, b) => {
                  const da = a.dataExecucao?.toDate ? a.dataExecucao.toDate() : (a.dataExecucao ? new Date(a.dataExecucao) : null);
                  const dbv = b.dataExecucao?.toDate ? b.dataExecucao.toDate() : (b.dataExecucao ? new Date(b.dataExecucao) : null);
                  if (!da && !dbv) return 0;
                  if (!da) return 1;
                  if (!dbv) return -1;
                  return dbv.getTime() - da.getTime();
                });

              const execComFotos = execs.find((e) => Array.isArray(e?.fotos) && e.fotos.length > 0);
              if (!execComFotos) return [];

              const fotosComDataUrl = await Promise.all(
                (execComFotos.fotos || []).map(async (foto, idx) => {
                  const url = foto?.url || "";
                  const dataUrl = await toDataUrl(url);
                  return {
                    url,
                    dataUrl,
                    nome: foto?.nome || `Foto ${idx + 1}`,
                    preventivaNome: execComFotos.preventivaNome || prev?.nome || "Preventiva",
                    maquinaNome: execComFotos.maquinaNome || prev?.maquinaNome || "Equipamento",
                    modelo: prev?.modelo || prev?.modeloPreventivaCodigo || prev?.periodicidade || "",
                  };
                })
              );

              return fotosComDataUrl.filter((foto) => !!foto.dataUrl || !!foto.url);
            } catch {
              return [];
            }
          })
        );

        const urls = new Set();
        fotosPreventivas = colecoes
          .flat()
          .filter((foto) => {
            const key = foto.dataUrl || foto.url;
            if (!key || urls.has(key)) return false;
            urls.add(key);
            return true;
          })
          .slice(0, 8);
      }

      const fotosPorEquipamento = fotosPreventivas.reduce((acc, foto) => {
        const titulo = [foto.maquinaNome || "Equipamento", foto.modelo || ""].filter(Boolean).join(" • ");
        if (!acc[titulo]) acc[titulo] = [];
        acc[titulo].push(foto);
        return acc;
      }, {});

      const fotosDepoisHtml = fotosPreventivas.length
        ? Object.entries(fotosPorEquipamento)
          .map(([tituloEquip, fotos]) => `
            <div class="foto-equip-bloco">
              <div class="foto-equip-titulo">${escapeHtml(tituloEquip)}</div>
              <div class="fotos-equip-grid">
                ${fotos.map((foto, idx) => `
                  <div class="foto-item">
                    <div style="font-size:10px;color:#4b5563;">${escapeHtml(foto.preventivaNome || "Preventiva")} • Foto ${idx + 1}</div>
                    <img src="${escapeHtml(foto.dataUrl || foto.url)}" style="width:100%;max-height:220px;object-fit:contain;border:1px solid #e5e7eb;border-radius:6px;">
                  </div>
                `).join("")}
              </div>
            </div>
          `).join("")
        : `<div style="font-size:11px;color:#6b7280;">Nenhuma foto de preventiva encontrada.</div>`;

      const corpoHtml = `
    <h2>1. Dados do Atendimento</h2>
    <div class="linha">
      <div class="col">
        <div class="label">Cliente</div>
        <div class="valor">${escapeHtml(clienteNome)}</div>
      </div>
      <div class="col">
        <div class="label">Academia / Unidade</div>
        <div class="valor">${escapeHtml(academiaNome)}</div>
      </div>
    </div>
    <div class="linha">
      <div class="col">
        <div class="label">Equipamento</div>
        <div class="valor">${escapeHtml(equipamentoDescricao)}</div>
      </div>
      <div class="col">
        <div class="label">Responsável no local</div>
        <div class="valor">${escapeHtml(tecnicoNome)}</div>
      </div>
    </div>
    <div class="linha">
      <div class="col">
        <div class="label">Data início</div>
        <div class="valor">${escapeHtml(dataInicioBR)}</div>
      </div>
      <div class="col">
        <div class="label">Data final</div>
        <div class="valor">${escapeHtml(dataFimBR)}</div>
      </div>
    </div>
    <div class="linha">
      <div class="col">
        <div class="label">Horário</div>
        <div class="valor">${escapeHtml(horaInicio || "-")} às ${escapeHtml(horaFim || "-")} (${escapeHtml(totalHoras)})</div>
      </div>
    </div>

    <h2>2. Diagnóstico</h2>
    <div class="linha">
      <div class="col">
        <div class="label">Tipo de defeito principal</div>
        <div class="valor">${escapeHtml(atendimentoSelecionado.status || "-")}</div>
      </div>
      <div class="col">
        <div class="label">Gravidade</div>
        <div class="valor">BAIXA</div>
      </div>
    </div>
    <div class="bloco-texto">${escNl(defeitosTexto)}</div>

    <h2>3. Serviços executados</h2>
    <div class="bloco-texto">${escNl(servicosExecutados)}</div>

    <h2>4. Peças substituídas</h2>
    <div class="bloco-texto">-</div>

    <h2>5. Recomendações • Avaliação</h2>
    <div class="linha">
      <div class="col">
        <div class="label">Avaliação geral (1 a 5)</div>
        <div class="valor">3</div>
      </div>
    </div>
    <div class="label" style="margin-top:4px;">Recomendações ao cliente</div>
    <div class="bloco-texto">-</div>

    <h2>6. Financeiro & Deslocamento</h2>
    <div class="linha">
      <div class="col">
        <div class="label">Km rodados</div>
        <div class="valor">0 km (R$ 1,20/km)</div>
      </div>
    </div>
    <table class="tabela-fin">
      <tr><th>Descrição</th><th>Valor (R$)</th></tr>
      <tr><td>Mão de obra</td><td>0,00</td></tr>
      <tr><td>Peças</td><td>0,00</td></tr>
      <tr><td>Deslocamento (km × 1,20)</td><td>0,00</td></tr>
      <tr><td>Pedágio</td><td>0,00</td></tr>
      <tr><td>Desconto</td><td>- 0,00</td></tr>
      <tr class="total"><td>VALOR TOTAL</td><td>R$ 0,00</td></tr>
    </table>

    <h2>7. Observações gerais</h2>
    <div class="bloco-texto">${escNl(sessaoBase?.observacoes || "-")}</div>

    <h2>8. Registro fotográfico</h2>
    <div class="grid-fotos">
      <div>
        <div class="label">ANTES</div>
        <div class="bloco-texto" style="margin-bottom:6px;">-</div>
        <div style="font-size:11px;color:#6b7280;">Sem fotos ANTES para planejamento.</div>
      </div>
      <div>
        <div class="label">DEPOIS</div>
        <div class="bloco-texto" style="margin-bottom:6px;">Fotos capturadas nas execuções de preventivas vinculadas à sessão.</div>
        ${fotosDepoisHtml}
      </div>
    </div>

    <h2>9. Condições de garantia</h2>
    <div class="bloco-texto">Garantia de 30 dias sobre o serviço de manutenção executado, limitada às intervenções realizadas.</div>

    <div class="assinaturas">
      <div class="ass-box">
        <div class="linha-ass"></div>
        <div>Responsável Técnico<br>${escapeHtml(tecnicoNome)}</div>
      </div>
      <div class="ass-box">
        <div class="linha-ass"></div>
        <div>Cliente / Academia<br>-</div>
      </div>
    </div>`;

      html = `<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8">
  <title>${escapeHtml(`${tipoRelLabel} - ${EMPRESA.nome}`)}</title>
  <style>
    body {
      font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
      margin: 0;
      background: #e5e7eb;
      color: #111827;
      font-size: 12px;
    }
    .pagina {
      max-width: 820px;
      margin: 24px auto;
      background: #ffffff;
      border-radius: 10px;
      box-shadow: 0 10px 30px rgba(0,0,0,0.12);
      padding: 18px 22px 20px;
      box-sizing: border-box;
    }
    .faixa-topo {
      background: linear-gradient(135deg, #020617, #111827 60%, #f97316 100%);
      border-radius: 8px;
      padding: 10px 14px;
      color: #f9fafb;
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 10px;
    }
    .marca { display: flex; flex-direction: column; }
    .sub { font-size: 10px; color: #e5e7eb; }
    .dados-empresa {
      text-align: right;
      font-size: 10px;
      line-height: 1.3;
      color: #e5e7eb;
    }
    .dados-empresa span { display: block; }
    h2 {
      font-size: 13px;
      margin: 10px 0 4px;
      padding-bottom: 2px;
      border-bottom: 2px solid #f97316;
      text-transform: uppercase;
      letter-spacing: 0.08em;
      color: #111827;
    }
    .linha {
      display: flex;
      justify-content: space-between;
      gap: 12px;
      margin-bottom: 4px;
    }
    .col { flex: 1; }
    .label { font-weight: 600; font-size: 11px; color: #374151; }
    .valor { font-size: 11px; color: #111827; }
    .bloco-texto {
      border: 1px solid #e5e7eb;
      border-radius: 6px;
      padding: 6px;
      min-height: 40px;
      white-space: pre-wrap;
      font-size: 11px;
      background: #f9fafb;
    }
    .tabela-fin {
      width: 100%;
      border-collapse: collapse;
      margin-top: 4px;
      font-size: 11px;
    }
    .tabela-fin th, .tabela-fin td {
      border: 1px solid #e5e7eb;
      padding: 4px 6px;
      text-align: left;
    }
    .tabela-fin th { background: #f3f4f6; }
    .tabela-fin tr:nth-child(odd) td { background: #f9fafb; }
    .total { font-weight: 700; background: #fef3c7 !important; }
    .grid-fotos { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
    .foto-equip-bloco { margin-bottom: 10px; }
    .foto-equip-titulo {
      font-size: 11px;
      font-weight: 700;
      color: #111827;
      margin: 4px 0 6px;
    }
    .fotos-equip-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 8px;
    }
    .foto-item { break-inside: avoid; }
    .assinaturas {
      display: flex;
      justify-content: space-between;
      margin-top: 22px;
      font-size: 11px;
    }
    .ass-box { width: 48%; text-align: center; }
    .linha-ass {
      border-top: 1px solid #9ca3af;
      margin-top: 26px;
      padding-top: 2px;
    }
    .rodape {
      margin-top: 16px;
      font-size: 9px;
      text-align: center;
      color: #6b7280;
    }
    @media print {
      body { background: #ffffff; }
      .pagina { box-shadow: none; margin: 0; max-width: 100%; border-radius: 0; }
    }
  </style>
</head>
<body>
  <div class="pagina">
    <div class="faixa-topo">
      <div class="marca">
        ${logoHtml}
        <div class="sub">${escapeHtml(`${EMPRESA.sub} • ${tipoRelLabel}`)}</div>
      </div>
      <div class="dados-empresa">
        <span>${escapeHtml(EMPRESA.nome)}</span>
        <span>CNPJ: ${escapeHtml(EMPRESA.cnpj)}</span>
        <span>Tel: ${escapeHtml(EMPRESA.telefone)}</span>
        <span>Instagram: ${escapeHtml(EMPRESA.instagram)}</span>
        <span>${escapeHtml(EMPRESA.email)}</span>
        <span>${escapeHtml(EMPRESA.site)}</span>
        <span>${escapeHtml(EMPRESA.endereco)}</span>
      </div>
    </div>

    ${corpoHtml}

    <div class="rodape">
      ${escapeHtml(`${EMPRESA.nome} • ${EMPRESA.sub} • ${tipoRelLabel}`)}
    </div>
  </div>
</body>
</html>`;

      const { default: html2pdf } = await import("html2pdf.js");

      const parser = new DOMParser();
      const parsed = parser.parseFromString(html, "text/html");

      const container = document.createElement("div");
      container.style.position = "fixed";
      container.style.left = "0";
      container.style.top = "0";
      container.style.width = "1100px";
      container.style.opacity = "0.01";
      container.style.pointerEvents = "none";
      container.style.zIndex = "-1";

      try {
        const stylesText = Array.from(parsed.querySelectorAll("style"))
          .map((s) => s.textContent || "")
          .join("\n");
        const styleTag = document.createElement("style");
        styleTag.setAttribute("data-relatorio-pdf", "true");
        styleTag.textContent = stylesText;
        document.head.appendChild(styleTag);

        const bodyWrap = document.createElement("div");
        bodyWrap.innerHTML = parsed.body?.innerHTML || "";
        container.appendChild(bodyWrap);

        document.body.appendChild(container);

        await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
        await new Promise((resolve) => setTimeout(resolve, 80));

        const nomeArquivo = `Relatorio_${clienteNome.replace(/[^a-zA-Z0-9]/g, "_")}_${atendimentoSelecionado.competencia?.replace(/[^a-zA-Z0-9]/g, "_") || "sem_competencia"}_${Date.now()}.pdf`;

        await html2pdf()
          .set({
            filename: nomeArquivo,
            margin: 0,
            image: { type: "jpeg", quality: 0.98 },
            html2canvas: {
              scale: 2,
              useCORS: true,
              logging: false,
              backgroundColor: "#0b1120",
            },
            jsPDF: {
              unit: "mm",
              format: "a4",
              orientation: "portrait",
            },
            pagebreak: {
              mode: ["css", "legacy"],
            },
          })
          .from(bodyWrap)
          .save();
      } finally {
        const styleTemp = document.head.querySelector('style[data-relatorio-pdf="true"]');
        if (styleTemp) {
          styleTemp.remove();
        }
        if (container.parentNode) {
          container.parentNode.removeChild(container);
        }
      }

      if (sessaoBase?.id) {
        try {
          const sessaoRef = doc(db, "atendimentos_sessoes", sessaoBase.id);
          await updateDoc(sessaoRef, {
            status: "FINALIZADA",
            updatedAt: serverTimestamp(),
            finalizadaEm: serverTimestamp(),
          });
          await marcarPropostasFinalizadasPorSessao(sessaoBase.id);
          await carregarAtendimentos();
        } catch (statusErr) {
          console.warn("Falha ao marcar sessão como FINALIZADA:", statusErr);
        }
      }
      setSucesso("Relatório PDF gerado e visita finalizada com sucesso!");
    } catch (e) {
      console.error("Erro ao gerar relatório:", e);
      setErro(`Erro ao gerar relatório: ${e.message || "Tente novamente"}`);
    } finally {
      setGerandoRelatorio(false);
    }
  };

  if (isMobile) {
    return (
      <LayoutMobile
        tema={tema}
        alterarTema={alterarTema}
        titulo="Planejamento"
        abaBottom="home"
        sidebarAberta={sidebarAberta}
        onToggleSidebar={() => setSidebarAberta((prev) => !prev)}
      >
        <div className="m-alert m-alert-erro">
          O módulo Planejamento está disponível apenas no desktop.
        </div>
      </LayoutMobile>
    );
  }

  return (
    <LayoutDesktop
      tema={tema}
      titulo="Planejamento"
      subtitulo="Atendimentos (Visitas)"
    >
      <section className="pc-card pc-table-card">
        <header className="pc-table-header">
          <div className="pc-table-header-text">
            <h3>Atendimentos (Visitas)</h3>
            <p>Planeje visitas por cliente, competencia e sessoes.</p>
          </div>
          <div className="pc-table-header-actions" style={{ position: "relative", zIndex: 5, display: "flex", gap: 8 }}>
            <button className="pc-btn-primario" type="button" onClick={abrirNovaVisita}>
              Nova Visita
            </button>
            {canAgendamentoTecnicos && (
              <button className="pc-btn-secundario" type="button" onClick={abrirAgendamentoTecnicos}>
                Agendamento Técnicos
              </button>
            )}
          </div>
        </header>

        {erro && <div className="pc-alert pc-alert-erro">{erro}</div>}
        {sucesso && <div className="pc-alert pc-alert-sucesso">{sucesso}</div>}

        <div className="planejamento-visitas-filtros">
          <label>
            Cliente
            <select value={clienteId} onChange={(e) => setClienteId(e.target.value)}>
              <option value="">Selecione</option>
              {clientes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nome || c.razaoSocial || "Cliente"}
                </option>
              ))}
            </select>
          </label>
          <label>
            Academia
            <select
              value={academiaId}
              onChange={(e) => setAcademiaId(e.target.value)}
              disabled={!clienteId}
            >
              <option value="">Todas</option>
              {academias.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.nome || a.codigo || a.id}
                </option>
              ))}
            </select>
          </label>
          <label>
            Competencia
            <input
              type="month"
              value={competenciaMes}
              onChange={(e) => setCompetenciaMes(e.target.value)}
            />
          </label>
          <label>
            Quinzena
            <select value={quinzenaFiltro} onChange={(e) => setQuinzenaFiltro(e.target.value)}>
              <option value="TODAS">Todas</option>
              <option value="Q1">Q1</option>
              <option value="Q2">Q2</option>
            </select>
          </label>
          <label>
            Status
            <select value={statusFiltro} onChange={(e) => setStatusFiltro(e.target.value)}>
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="pc-table-wrapper">
          <table className="pc-table">
            <thead>
              <tr>
                <th style={{ width: 44 }} />
                <th>Cliente</th>
                <th>Academia</th>
                <th>Competencia</th>
                <th>Inicio</th>
                <th>Tecnico</th>
                <th>Status</th>
                <th>Itens</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {(loadingAtendimentos || loadingVisitas) && (
                <tr>
                  <td colSpan={9}>Carregando visitas...</td>
                </tr>
              )}
              {!loadingAtendimentos && !loadingVisitas && visitas.length === 0 && (
                <tr>
                  <td colSpan={9}>Nenhuma visita encontrada.</td>
                </tr>
              )}
              {!loadingAtendimentos && !loadingVisitas && visitas.map((v) => {
                const isVisitaExpandida =
                  atendimentoSelecionado?.id === v.atendimentoId
                  && sessaoSelecionada?.id === v.id;

                return (
                <tr key={v.id} className={isVisitaExpandida ? "planejamento-visitas-sessao-ativa" : ""}>
                  <td>
                    <button
                      type="button"
                      className="pc-expand-btn"
                      aria-label={isVisitaExpandida ? "Recolher visita" : "Expandir visita"}
                      onClick={() => abrirVisita(v)}
                    >
                      <span className={isVisitaExpandida ? "pc-expand-chevron open" : "pc-expand-chevron"}>▸</span>
                    </button>
                  </td>
                  <td>{clientesById[v.clienteId]?.nome || "-"}</td>
                  <td>{v.academiaNome || academiasMap[v.academiaId] || "-"}</td>
                  <td>{v.competencia || "-"}</td>
                  <td>{formatDateTime(v.inicioEm)}</td>
                  <td>{getTecnicoNome(v.tecnicoId)}</td>
                  <td>{v.status || "-"}</td>
                  <td>
                    <span
                      className={`planejamento-visitas-badge ${(v.itensCount ?? 0) === 0 ? "is-empty" : ""}`}
                    >
                      {v.itensCount ?? 0} itens
                    </span>
                  </td>
                  <td>
                    {canDeleteSessao && (
                      <button
                        type="button"
                        className="pc-btn-perigo"
                        onClick={() => handleExcluirSessao(v.id, v.atendimentoId)}
                        disabled={excluindoSessao}
                        style={{ marginLeft: 8 }}
                      >
                        {excluindoSessao ? "Excluindo..." : "Deletar"}
                      </button>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {atendimentoSelecionado && (
        <section className="pc-card planejamento-visitas-detalhe">
          <header className="planejamento-visitas-detalhe-header">
            <div>
              <h3>Atendimento</h3>
              <p>
                {clientesById[atendimentoSelecionado.clienteId]?.nome || "Cliente"} • {atendimentoSelecionado.competencia}
              </p>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button
                type="button"
                className="pc-btn-primario"
                onClick={() => navigateWithFallback(novaVisitaHref)}
              >
                Criar visita (com itens)
              </button>
              {atendimentoSelecionado.status !== "CONCLUIDO" && (
                <button
                  type="button"
                  className="pc-btn-primario"
                  onClick={handleConcluirAtendimento}
                  disabled={concluindoAtendimento}
                  title="Marcar atendimento como concluído"
                >
                  {concluindoAtendimento ? "Concluindo..." : "✓ Concluir Atendimento"}
                </button>
              )}
              <button
                type="button"
                className="pc-btn-secundario"
                onClick={handleGerarRelatorio}
                disabled={gerandoRelatorio}
                title="Baixar relatório do atendimento"
              >
                {gerandoRelatorio ? "Gerando..." : "📄 Gerar Relatório"}
              </button>
            </div>
          </header>

          <div className="planejamento-visitas-resumo">
            <div>
              <span>Contrato</span>
              <strong>{atendimentoSelecionado.contratoTipo || "-"}</strong>
            </div>
            <div>
              <span>Status</span>
              <strong>{atendimentoSelecionado.status || "-"}</strong>
            </div>
            <div>
              <span>Competencia</span>
              <strong>{atendimentoSelecionado.competencia || "-"}</strong>
            </div>
            <div>
              <span>Progresso</span>
              <strong>
                {atendimentoSelecionado.progresso?.equipamentosConcluidos != null
                  ? `${atendimentoSelecionado.progresso.equipamentosConcluidos}/${atendimentoSelecionado.progresso.equipamentosAlvo || 0}`
                  : "-"}
              </strong>
            </div>
          </div>

          <div className="pc-table-wrapper">
            <table className="pc-table">
              <thead>
                <tr>
                  <th style={{ width: 44 }} />
                  <th>Inicio</th>
                  <th>Fim</th>
                  <th>Tecnico</th>
                  <th>Status</th>
                  <th>Observacoes</th>
                  <th>Itens</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {loadingSessoes && (
                  <tr>
                    <td colSpan={8}>Carregando sessoes...</td>
                  </tr>
                )}
                {!loadingSessoes && sessoes.length === 0 && (
                  <tr>
                    <td colSpan={8}>Nenhuma sessao cadastrada.</td>
                  </tr>
                )}
                {!loadingSessoes && sessoes.map((s) => {
                  const isExpanded = sessaoExpandidaId === s.id;
                  const itensExpandido = sessaoItensExpandidaMap[s.id] || [];
                  const loadingExpandido = loadingSessaoExpandidaId === s.id;

                  return (
                    <React.Fragment key={s.id}>
                      <tr className={isExpanded ? "planejamento-visitas-sessao-ativa" : ""}>
                        <td>
                          <button
                            type="button"
                            className="pc-expand-btn"
                            aria-label={isExpanded ? "Recolher" : "Expandir"}
                            onClick={() => handleToggleExpandirSessao(s)}
                          >
                            <span className={isExpanded ? "pc-expand-chevron open" : "pc-expand-chevron"}>▸</span>
                          </button>
                        </td>
                        <td>{formatDateTime(s.inicioEm)}</td>
                        <td>{formatDateTime(s.fimEm)}</td>
                        <td>{getTecnicoNome(s.tecnicoId)}</td>
                        <td>{s.status || "-"}</td>
                        <td>{s.observacoes || "-"}</td>
                        <td>
                          <span className="planejamento-visitas-badge">{sessaoItensCount[s.id] ?? 0} itens</span>
                        </td>
                        <td>
                          {canDeleteSessao && (
                            <button
                              type="button"
                              className="pc-btn-perigo"
                              onClick={() => handleExcluirSessao(s.id, atendimentoSelecionado.id)}
                              disabled={excluindoSessao}
                            >
                              {excluindoSessao ? "Excluindo..." : "Deletar"}
                            </button>
                          )}
                        </td>
                      </tr>

                      {isExpanded && (
                        <tr className="pc-expanded-row">
                          <td colSpan={8}>
                            <div className="pc-expand-panel planejamento-visitas-expand-panel">
                              <div className="planejamento-visitas-expand-header">
                                <strong>OS e Preventivas desta sessão</strong>
                                <span>{formatDateTime(s.inicioEm)}</span>
                              </div>

                              {loadingExpandido ? (
                                <div className="pc-alert">Carregando itens da sessão...</div>
                              ) : itensExpandido.length === 0 ? (
                                <div className="pc-alert">Nenhum item vinculado a esta sessão.</div>
                              ) : (
                                <div className="planejamento-visitas-expand-lista">
                                  {itensExpandido.map((item) => (
                                    <div key={item.id} className="planejamento-visitas-expand-item">
                                      <span className="planejamento-visitas-badge">{item.tipo}</span>
                                      <span style={{ fontWeight: 600 }}>{item.label || "-"}</span>
                                      <span style={{ opacity: 0.75 }}>{item.subLabel || ""}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

    </LayoutDesktop>
  );
}
